import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Plus, FolderOpen, FileText as FileIcon, Users, Clock } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import StatusBadge from "@/components/StatusBadge";
import { relativeTime, STATUS } from "@/lib/utils-app";

const StatCard = ({ label, value, hint, accent }) => (
  <div className="bg-white border border-zinc-200 rounded-sm p-6" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">{label}</div>
    <div className="mt-3 flex items-end justify-between">
      <span className={`text-4xl font-semibold tracking-tight ${accent || "text-zinc-900"}`} style={{ fontFamily: "Outfit" }}>
        {value}
      </span>
      <span className="text-xs text-zinc-500 pb-1">{hint}</span>
    </div>
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-fade-in-up">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold">Översikt</div>
          <h1 className="mt-2 text-4xl sm:text-5xl font-semibold tracking-tight text-zinc-900" style={{ fontFamily: "Outfit" }}>
            Hej {user?.name?.split(" ")[0] || ""}.
          </h1>
          <p className="mt-2 text-zinc-500">
            {user?.role === "admin" ? "Aktuell ärendebelastning i kommunen." : "Här är dina pågående bygglovsärenden."}
          </p>
        </div>
        <Link to="/cases?new=1" data-testid="cta-new-case"
          className="inline-flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white px-4 py-2.5 rounded-sm text-sm font-medium active:scale-[0.98] transition">
          <Plus className="w-4 h-4" /> Nytt ärende
        </Link>
      </div>

      {/* Stats */}
      <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Totalt" value={stats?.total ?? "–"} hint="ärenden" />
        <StatCard label="Inkommet" value={stats?.by_status?.inkommet ?? "–"} hint="att granska" accent="text-blue-700" />
        <StatCard label="Komplettering" value={stats?.by_status?.komplettering ?? "–"} hint="kräver åtgärd" accent="text-amber-700" />
        <StatCard label="Beslut" value={stats?.by_status?.beslut ?? "–"} hint="beslutade" accent="text-emerald-700" />
      </div>

      {/* Status overview row */}
      <div className="mt-10">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-medium tracking-tight text-zinc-900" style={{ fontFamily: "Outfit" }}>Status-fördelning</h2>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {Object.entries(STATUS).map(([k, s]) => (
            <div key={k} className={`p-4 border rounded-sm ${s.classes}`}>
              <div className="text-xs uppercase tracking-wider font-semibold">{s.label}</div>
              <div className="mt-2 text-2xl font-semibold" style={{ fontFamily: "Outfit" }}>
                {stats?.by_status?.[k] ?? 0}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent */}
      <div className="mt-12">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-medium tracking-tight text-zinc-900" style={{ fontFamily: "Outfit" }}>Senaste aktivitet</h2>
          <Link to="/cases" data-testid="view-all-cases" className="text-sm text-blue-700 hover:underline flex items-center gap-1">
            Visa alla <ArrowUpRight className="w-3.5 h-3.5" />
          </Link>
        </div>
        <div className="bg-white border border-zinc-200 rounded-sm divide-y divide-zinc-100">
          {(stats?.recent || []).length === 0 && (
            <div className="px-6 py-10 text-center text-sm text-zinc-500">Inga ärenden ännu.</div>
          )}
          {(stats?.recent || []).map((c) => (
            <Link
              key={c.case_id}
              to={`/cases/${c.case_id}`}
              data-testid={`recent-${c.case_id}`}
              className="flex items-center justify-between gap-4 px-6 py-4 hover:bg-zinc-50 transition"
            >
              <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">{c.ref}</div>
                <div className="mt-1 font-medium text-zinc-900 truncate">{c.title}</div>
                <div className="text-xs text-zinc-500 truncate">{c.address}</div>
              </div>
              <StatusBadge status={c.status} />
              <span className="hidden sm:flex items-center gap-1 text-xs text-zinc-500 w-28 justify-end">
                <Clock className="w-3 h-3" /> {relativeTime(c.updated_at)}
              </span>
            </Link>
          ))}
        </div>
      </div>

      {/* Quick links */}
      <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { to: "/cases", icon: FolderOpen, label: "Alla ärenden", desc: "Filtrera och sök" },
          { to: "/documents", icon: FileIcon, label: "Dokument", desc: "Ritningar, intyg, PDF" },
          { to: "/contacts", icon: Users, label: "Kontakter", desc: "Sökande, handläggare, entreprenörer" },
        ].map((q) => (
          <Link
            key={q.to}
            to={q.to}
            data-testid={`quick-${q.to.replace("/", "")}`}
            className="group bg-white border border-zinc-200 rounded-sm p-5 hover:border-blue-400 transition"
          >
            <q.icon className="w-5 h-5 text-blue-700 stroke-[1.5]" />
            <div className="mt-4 font-medium text-zinc-900 group-hover:text-blue-700 transition">{q.label}</div>
            <div className="text-sm text-zinc-500">{q.desc}</div>
          </Link>
        ))}
      </div>
    </div>
  );
}
